<template>
  <Loading :loading="isSectionLoading" loading-text="Loading date overrides...">
    <div>
      <div class="flex justify-between w-full mb-4">
        <div>
          <div class="flex items-center">
            <img src="@/assets/my-calendar/availability/black-calendar.svg" alt="color" />
            <div class="card-title ml-2">Date Overrides</div>
          </div>
          <div class="mt-1 text-[12px] text-[#999999]">
            Overrides replace the default schedule for that date
          </div>
        </div>
        <ResponsiveButton
          data-testid="my-calendar.date-override-section.add-hours-btn"
          button_type="outlined"
          built_in_theme="dark"
          size="medium"
          display_name="Add Hours"
          @click-button="$emit('open-dialog')"
        />
      </div>

      <div
        v-for="exception in exceptions"
        :key="exception.exception_id"
        :data-testid="`my-calendar.date-override-section.override-row.${exception.exception_id}`"
        class="date-div"
      >
        <div :data-testid="`my-calendar.date-override-section.date-text.${exception.exception_id}`" class="date-cell">{{ formatExceptionDate(exception.date) }}</div>
        <div :data-testid="`my-calendar.date-override-section.time-text.${exception.exception_id}`" class="time-cell">{{ formatExceptionTime(exception.start_time, exception.end_time) }}</div>
        <div class="action-group">
          <div :data-testid="`my-calendar.date-override-section.edit-btn.${exception.exception_id}`" class="action-btn cursor-pointer flex items-center" @click="handleEditException(exception)">
            <img src="@/assets/my-calendar/task/edit.svg" alt="edit" class="mr-1" />
            <span class="text-sm text-[#666666]">Edit</span>
          </div>
          <div
            :data-testid="`my-calendar.date-override-section.delete-btn.${exception.exception_id}`"
            class="action-btn cursor-pointer flex items-center"
            @click="openDeleteDialog(exception)"
          >
            <img src="@/assets/my-calendar/task/red-delete.svg" alt="delete" class="mr-1" />
            <span class="text-sm text-[#666666]">Delete</span>
          </div>
        </div>
      </div>

      <Dialog
        :model-value="showDeleteDialog"
        title="Delete Date Override"
        dialog-type="error"
        dialog-size="small"
        :header-icon-src="deleteDialogIcon"
        :close-on-overlay="!isDeleteDialogLoading"
        @update:model-value="(value) => { if (!value) closeDeleteDialog() }"
        @close="closeDeleteDialog"
      >
        <Loading :loading="isDeleteDialogLoading" loading-text="Deleting...">
          <div data-testid="my-calendar.date-override-section.delete-dialog-message-text" class="delete-dialog-body">
            Are you sure you want to delete <strong>{{ deleteItemName }}</strong>? This action cannot be undone.
          </div>
        </Loading>

        <template #footer>
          <ResponsiveButton
            data-testid="my-calendar.date-override-section.delete-dialog-cancel-btn"
            button_type="outlined"
            built_in_theme="error"
            size="medium"
            display_name="Cancel"
            :disabled="isDeleteDialogLoading"
            @click-button="closeDeleteDialog"
          />
          <ResponsiveButton
            data-testid="my-calendar.date-override-section.delete-dialog-confirm-btn"
            button_type="filled"
            built_in_theme="error"
            size="medium"
            display_name="Delete"
            :disabled="isDeleteDialogLoading"
            @click-button="handleDeleteConfirm"
          />
        </template>
      </Dialog>
    </div>
  </Loading>
</template>

<script setup>
import { computed, ref } from 'vue';
import moment from 'moment';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import deleteDialogIcon from '@/assets/MyWorkBench/archive/delete.svg';

const props = defineProps({
  exceptions: {
    type: Array,
    default: () => [],
  },
  deleteException: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['open-dialog', 'edit-exception']);
const isSectionLoading = ref(false);
const isDeleteDialogLoading = ref(false);
const showDeleteDialog = ref(false);
const pendingDeleteException = ref(null);

function formatExceptionTime(start, end) {
  if (!start || !end) return '00:00AM - 11:59PM';
  const startTime = moment(start, 'HH:mm:ss');
  const endTime = moment(end, 'HH:mm:ss');
  if (!startTime.isValid() || !endTime.isValid()) return 'N/A';
  return `${startTime.format('hh:mmA')} - ${endTime.format('hh:mmA')}`;
}

function formatExceptionDate(date) {
  if (!date) return 'N/A';
  const dateObj = moment(date, 'YYYY-MM-DD');
  if (!dateObj.isValid()) return 'N/A';
  return dateObj.format('MMM DD, YYYY');
}

function handleEditException(exception) {
  emit('edit-exception', exception);
}

function openDeleteDialog(exception) {
  pendingDeleteException.value = exception;
  showDeleteDialog.value = true;
}

function closeDeleteDialog() {
  if (isDeleteDialogLoading.value) return;
  showDeleteDialog.value = false;
  pendingDeleteException.value = null;
}

const deleteItemName = computed(() => {
  if (!pendingDeleteException.value) return '';
  return formatExceptionDate(pendingDeleteException.value.date);
})

async function handleDeleteConfirm() {
  if (!pendingDeleteException.value) return;
  isDeleteDialogLoading.value = true;
  try {
    await props.deleteException(pendingDeleteException.value);
    pendingDeleteException.value = null;
    showDeleteDialog.value = false;
  } catch (error) {
    console.error('Error deleting exception:', error);
  } finally {
    isDeleteDialogLoading.value = false;
  }
}
</script>

<style scoped>
.card-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
}

.date-div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #F9F9F9;
  padding: 12px;
  margin-top: 4px;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  border-radius: 4px;
}

.date-cell {
  color: #333333;
  min-width: 110px;
}

.time-cell {
  color: #333333;
  flex: 1;
  text-align: center;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.delete-dialog-body {

  padding: 24px 32px;
}
</style>
